import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { LocalScanCatalogRefreshTrigger } from '@/utils/sentry/attributes';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface Props {
  cancelDownload: () => void;
  downloadProgress: number;
  hasData: boolean;
  isDownloading: boolean;
  startSync: (trigger?: LocalScanCatalogRefreshTrigger) => Promise<void>;
}

export const LocalScannerDownloadBanner: React.FC<Props> = ({
  cancelDownload,
  downloadProgress,
  hasData,
  isDownloading,
  startSync,
}) => {
  const [styles] = useStyles();

  if (hasData && !isDownloading) {
    return null;
  }

  const isEmpty = !hasData && !isDownloading;

  const bannerText = isEmpty
    ? i18n.t('scan.download_catalog_data_prompt')
    : i18n.t('scan.syncing_catalog_data', {
        percentage: downloadProgress,
      });

  const button = isEmpty ? (
    <Button
      onPress={() => startSync(LocalScanCatalogRefreshTrigger.FirstOpen)}
      trackingData={{ name: 'scanner.download_catalog_button' }}
      type="tertiary"
    >
      {i18n.t('download')}
    </Button>
  ) : (
    <Button
      onPress={cancelDownload}
      trackingData={{ name: 'scanner.stop_catalog_sync_button' }}
      type="tertiary"
    >
      {i18n.t('stop_sync')}
    </Button>
  );

  return (
    <View style={styles.base}>
      <View style={styles.content}>
        {!isEmpty ? <ActivityIndicator /> : null}
        <Typography variant="paragraph-md">{bannerText}</Typography>
      </View>
      <View style={styles.buttonContainer}>{button}</View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.highlight.tertiary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  buttonContainer: {
    flexGrow: 0,
  },
  content: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
